import React from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';

function Footer() {
  const { getFooterText, getTagline, getCompanyName, getDisplayBranding, getFooterBranding } = useConfig();
  const branding = getDisplayBranding();
  const footerBranding = getFooterBranding();

  // Render the "Powered by DeedStack" branding based on tier
  const renderPoweredByBranding = () => {
    if (!footerBranding.show) return null;

    const styles = {
      prominent: {
        display: 'block',
        marginTop: '1.5rem',
        padding: '0.75rem 1.5rem',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '6px',
        textAlign: 'center'
      },
      badge: {
        display: 'inline-block',
        marginTop: '1rem',
        padding: '0.4rem 1rem',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '20px',
        fontSize: '0.8rem'
      },
      small: {
        display: 'inline-block',
        marginTop: '0.75rem',
        fontSize: '0.75rem',
        opacity: 0.7
      }
    };

    return (
      <div style={styles[footerBranding.style]}>
        <a
          href={footerBranding.link}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'white',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {footerBranding.style === 'prominent' && (
            <img
              src="/images/deedstack-logos/logo-icon-final.svg"
              alt="DeedStack"
              style={{ height: '24px', width: 'auto' }}
            />
          )}
          <span>{footerBranding.text}</span>
        </a>
      </div>
    );
  };

  // Render the footer logo/text based on tier
  // For FREE_TRIAL (deedstack type), return null - only show "Powered by DeedStack" badge
  const renderFooterBrand = () => {
    // For Free Trial, don't show company branding - only the DeedStack badge
    if (branding.type === 'deedstack') {
      return null;
    }

    if (branding.type === 'text') {
      return (
        <span style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: 'white',
          display: 'inline-block',
          marginBottom: '0.5rem'
        }}>
          {branding.text}
        </span>
      );
    }

    // Custom logo
    return (
      <img
        src={branding.lightSrc || branding.src}
        alt={getCompanyName()}
        style={{
          maxWidth: '180px',
          maxHeight: '50px',
          objectFit: 'contain'
        }}
        onError={(e) => {
          console.error('[Footer] Logo failed to load:', e.target.src);
          e.target.style.display = 'none';
        }}
      />
    );
  };

  return (
    <footer style={{
      backgroundColor: 'var(--forest-green)',
      color: 'white',
      padding: '3rem 1rem 1rem',
      marginTop: '4rem'
    }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          {/* Company */}
          <div>
            <h4 style={{ marginBottom: '1rem', color: 'var(--sandy-gold)', fontSize: '1.1rem' }}>Company</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/about" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  About Us
                </Link>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/contact" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Properties */}
          <div>
            <h4 style={{ marginBottom: '1rem', color: 'var(--sandy-gold)', fontSize: '1.1rem' }}>Properties</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/properties" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Browse Properties
                </Link>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/sold-properties" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Sold Properties
                </Link>
              </li>
            </ul>
          </div>

          {/* Account */}
          <div>
            <h4 style={{ marginBottom: '1rem', color: 'var(--sandy-gold)', fontSize: '1.1rem' }}>Account</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/login" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Login
                </Link>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/register" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Register
                </Link>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Dashboard
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 style={{ marginBottom: '1rem', color: 'var(--sandy-gold)', fontSize: '1.1rem' }}>Legal</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/privacy" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Privacy Policy
                </Link>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/terms" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem' }}>
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Logo and Copyright */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.2)',
          paddingTop: '1.5rem',
          textAlign: 'center',
          fontSize: '0.9rem'
        }}>
          {/* Only show company brand link for non-free-trial tiers */}
          {branding.type !== 'deedstack' && (
            <Link to="/" style={{ display: 'inline-block', marginBottom: '1rem' }}>
              {renderFooterBrand()}
            </Link>
          )}
          <p style={{ margin: 0, marginBottom: '0.5rem' }}>
            {getFooterText()}
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>
            {getTagline()}
          </p>

          {/* Tier-based "Powered by DeedStack" branding */}
          {renderPoweredByBranding()}
        </div>
      </div>
    </footer>
  );
}

export default Footer;